import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Button from 'UIKit/Button'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import styled from 'styled-components'

import { Icons } from 'svgs'

import { useEffect, useState } from 'react'
import { sanitize } from 'utils/html'
import { StyledHeading } from '../../Heading'
import { useDetectScroll, useScrollControl } from './hooks'

type ScrollBlockListProps = {
  title?: string
  items: any[]
  ItemBlock: React.FC<{ item: any } & BoxProps>
  numberOfItems?: number
} & BoxProps

const GAP = 12

const ScrollBlockList: React.FC<ScrollBlockListProps> = ({ items, title, ItemBlock, numberOfItems = 4, ...props }) => {
  const { containerRef, scrollRight, scrollLeft, scroll } = useScrollControl()
  const isDisplayScroll = useDetectScroll(containerRef)

  const [containerWidth, setContainerWidth] = useState(0)

  const itemWidth = containerWidth ? (containerWidth - GAP * (numberOfItems - 1)) / numberOfItems : 0

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const updateWidth = () => setContainerWidth(el.offsetWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return (
    <Box width="100%" mx="auto" {...props}>
      <RowBetween my="8px" className="scroll__header" justifyContent={title ? 'space-between' : 'flex-end !important'}>
        {title && (
          <StyledHeading
            as={'h3'}
            level={3}
            dangerouslySetInnerHTML={{ __html: sanitize(title) }}
            my="0px !important"
          />
        )}

        {isDisplayScroll && (
          <RowMiddle width="fit-content" className="scroll__control">
            <StyledIconButton onClick={scrollLeft} disabled={scroll?.start}>
              <Icons.ChevronLeftIcon width="16px" height="16px" />
            </StyledIconButton>
            <StyledIconButton onClick={scrollRight} disabled={scroll?.end} style={{ marginLeft: '8px' }}>
              <Icons.ChevronRightIcon width="16px" height="16px" />
            </StyledIconButton>
          </RowMiddle>
        )}
      </RowBetween>

      <ScrollContainer ref={containerRef} className="scroll__container" my="8px">
        {items?.map((item, index) => (
          <ItemWrapper key={index} width={itemWidth}>
            <ItemBlock item={item} width={`${itemWidth}px`} height={`${itemWidth}px`} />
          </ItemWrapper>
        ))}
      </ScrollContainer>
    </Box>
  )
}

const ScrollContainer = styled(Box)`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  gap: ${GAP}px;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`

const ItemWrapper = styled(Box)`
  scroll-snap-align: start;
  flex: 0 0 auto;

  width: fit-content;
`

const StyledIconButton = styled(Button)`
  background: #42475180 !important;
  border-radius: 50%;
  padding: 0;
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.mediaQueries.sm} {
    height: 32px;
    width: 32px;
  }

  svg {
    fill: ${({ theme }) => theme.colors.textSubtle};
    transform: scale(0.8);

    ${({ theme }) => theme.mediaQueries.sm} {
      transform: scale(1);
    }
  }

  &:hover svg {
    fill: ${({ theme }) => theme.colors.text};
  }

  &:disabled svg {
    fill: ${({ theme }) => theme.colors.textDisable} !important;
  }
`

export default ScrollBlockList
